import React, { FC, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { DayPoint, formatCount, formatWatchTime } from "./metrics";

/**
 * Seconds watched per day — the SAME series the old Analytics graph plotted
 * (videoAnalytics/helper.ts `getTotalViews`: per day, the sum of `seconds.length`
 * over that day's plays, labelled "Seconds"). Only the drawing changed; what is
 * drawn did not.
 *
 * Hand-drawn SVG rather than react-charts, for one reason: every colour here is
 * a token. The old chart carried three hardcoded hexes — `#fff` datum fill,
 * `#A58450` line, `#ebcea0` area — plus the library's default axis-label colour,
 * so the dots and the axis text both failed in dark mode. react-charts takes
 * literal colours through `getDatumStyle`/`getSeriesStyle`, and SVG PRESENTATION
 * ATTRIBUTES cannot resolve `var()`, so tokens have to arrive via the `style`
 * property — which is what every fill and stroke below does.
 *
 * The viewBox is measured, not fixed. A fixed one (the handoff's 1160-wide)
 * scales the whole drawing to fit its container, TEXT INCLUDED: at a 928px
 * panel a 10px label renders at 8px, and on a 375px screen at under 3px. Here
 * one viewBox unit is one CSS pixel at every width, so type is the size it says
 * it is and the tick count adapts instead of the glyphs shrinking.
 */

const HEIGHT = 232;
const PLOT_TOP = 8;
const PLOT_BOTTOM = 200;
const AXIS_GUTTER = 44;
const RIGHT_PAD = 6;

const anchorFor = (index: number, total: number): "start" | "middle" | "end" => {
  if (index === 0) return "start";
  if (index === total - 1) return "end";
  return "middle";
};

/**
 * Gridlines at a whole-number step, with the axis maximum derived FROM that step.
 *
 * Rounding the maximum to 1/2/5 × 10ⁿ and then labelling five fixed gridlines
 * with `Math.round(max * f)` put fractional quarter points on the lines, so each
 * printed label drifted off the line it sat on. Deriving the max from a whole
 * step keeps every label on its line.
 */
const niceAxis = (peak: number): { max: number; values: number[] } => {
  if (peak <= 0) return { max: 4, values: [0, 1, 2, 3, 4] };
  const raw = peak / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const n = raw / pow;
  const mult = [1, 2, 2.5, 5, 10].find((m) => n <= m) ?? 10;
  // Seconds are whole numbers, so the step is too.
  const step = Math.max(1, Math.round(mult * pow));
  const max = Math.ceil(peak / step) * step;
  const values: number[] = [];
  for (let v = 0; v <= max; v += step) values.push(v);
  return { max, values };
};

export const SecondsChart: FC<{ series: DayPoint[] }> = ({ series }) => {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(720);
  const [hover, setHover] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = hostRef.current;
    if (!el) return undefined;
    // A 280 floor combined with `maxWidth: 100%` meant the drawing was SCALED in any
    // narrower container, which broke the file's own premise and skewed the pointer
    // maths: nested on a 375px viewport the chart measures 249px, and hovering the
    // last day reported an index nine days short. Measure honestly instead.
    const apply = () => setWidth(Math.max(120, el.clientWidth));
    apply();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const plotLeft = AXIS_GUTTER;
  const plotRight = Math.max(plotLeft + 40, width - RIGHT_PAD);

  const { points, max, axisValues, areaPath, linePoints, peak } = useMemo(() => {
    const peakValue = series.reduce((m, p) => Math.max(m, p.seconds), 0);
    const axis = niceAxis(peakValue);
    const axisMax = axis.max;
    const span = Math.max(1, series.length - 1);
    const xs = (i: number) => plotLeft + ((plotRight - plotLeft) * i) / span;
    const ys = (v: number) => PLOT_BOTTOM - ((PLOT_BOTTOM - PLOT_TOP) * v) / (axisMax || 1);

    const pts = series.map((p, i) => ({ ...p, x: xs(i), y: ys(p.seconds) }));
    const line = pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    // A single point closed to `plotRight` drew a full-width wedge falling to zero,
    // inventing a decline that is not in the data — and that is the DEFAULT state
    // for a video whose first play was today. One point gets a dot, not an area.
    const area =
      pts.length > 1
        ? `M${pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" L")} L${plotRight},${PLOT_BOTTOM} L${plotLeft},${PLOT_BOTTOM} Z`
        : "";
    const peakPoint = peakValue > 0 ? pts.find((p) => p.seconds === peakValue) ?? null : null;

    return {
      points: pts,
      max: axisMax,
      axisValues: axis.values,
      areaPath: area,
      linePoints: line,
      peak: peakPoint,
    };
  }, [series, plotLeft, plotRight]);

  // Fewer ticks as the chart narrows, rather than the same five overlapping.
  const tickCount = useMemo(() => {
    if (width < 380) return 2;
    if (width < 560) return 3;
    if (width < 820) return 4;
    return 5;
  }, [width]);

  useEffect(() => setHover(null), [series]);

  if (series.length === 0) return null;

  const yTicks = axisValues.map((value) => ({
    value,
    y: PLOT_BOTTOM - ((PLOT_BOTTOM - PLOT_TOP) * value) / (max || 1),
  }));

  const xTickIdx = Array.from({ length: Math.min(tickCount, series.length) }, (_, i) =>
    Math.round((i * (series.length - 1)) / Math.max(1, Math.min(tickCount, series.length) - 1)),
  );

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (points.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    // Belt and braces below the 120 floor, where `maxWidth: 100%` can still scale
    // the drawing: convert CSS pixels to viewBox units before mapping to an index.
    const scale = rect.width > 0 ? width / rect.width : 1;
    const x = (e.clientX - rect.left) * scale;
    const span = Math.max(1, points.length - 1);
    const i = Math.round(((x - plotLeft) / (plotRight - plotLeft)) * span);
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };

  const active = hover != null ? points[hover] : null;

  return (
    <div ref={hostRef} className="relative w-full">
      <svg
        viewBox={`0 0 ${width} ${HEIGHT}`}
        width={width}
        height={HEIGHT}
        style={{ display: "block", maxWidth: "100%" }}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
        role="img"
        aria-label={`Seconds watched per day, ${series.length} days`}
      >
        {yTicks.map((t) => (
          <line
            key={`g-${t.y}`}
            x1={plotLeft}
            x2={plotRight}
            y1={t.y}
            y2={t.y}
            style={{ stroke: "var(--border)", strokeWidth: 1 }}
          />
        ))}
        {yTicks.map((t) => (
          <text
            key={`y-${t.y}`}
            x={plotLeft - 8}
            y={t.y + 4}
            textAnchor="end"
            style={{ fill: "var(--muted)", fontSize: 10 }}
          >
            {formatCount(t.value)}
          </text>
        ))}

        {areaPath && <path d={areaPath} style={{ fill: "var(--tint)" }} />}
        {/* One point renders nothing as a polyline, so it gets its own marker. */}
        {points.length === 1 && (
          <circle
            cx={points[0].x}
            cy={points[0].y}
            r={4}
            style={{ fill: "var(--surface)", stroke: "var(--accent)", strokeWidth: 2 }}
          />
        )}
        <polyline
          points={linePoints}
          style={{
            fill: "none",
            stroke: "var(--accent)",
            strokeWidth: 2,
            strokeLinejoin: "round",
            strokeLinecap: "round",
          }}
        />

        {points.length > 1 && peak && !active && (
          <circle
            cx={peak.x}
            cy={peak.y}
            r={4}
            style={{ fill: "var(--surface)", stroke: "var(--accent)", strokeWidth: 2 }}
          />
        )}

        {active && (
          <>
            <line
              x1={active.x}
              x2={active.x}
              y1={PLOT_TOP}
              y2={PLOT_BOTTOM}
              style={{ stroke: "var(--muted)", strokeWidth: 1, strokeDasharray: "3 3" }}
            />
            <circle
              cx={active.x}
              cy={active.y}
              r={4.5}
              style={{ fill: "var(--surface)", stroke: "var(--accent)", strokeWidth: 2 }}
            />
          </>
        )}

        {xTickIdx.map((i) => (
          <text
            key={`x-${i}`}
            x={points[i]?.x ?? 0}
            y={PLOT_BOTTOM + 22}
            // First and last labels anchor inward so they can't clip the plot.
            textAnchor={anchorFor(i, series.length)}
            style={{ fill: "var(--muted)", fontSize: 10 }}
          >
            {series[i]?.date.format("MMM D")}
          </text>
        ))}
      </svg>

      {active && (
        <div
          className="absolute pointer-events-none rounded-lg border border-border-token bg-surface px-2.5 py-2 whitespace-nowrap"
          style={{
            boxShadow: "var(--sh-2)",
            fontSize: 11.5,
            left: Math.min(Math.max(active.x, 70), Math.max(70, width - 70)),
            // Above the point when there is room, below it when there isn't.
            // Pinned to top:0 it sat directly over the marker on a peak day —
            // the one point the reader is most likely to hover.
            top: active.y > 70 ? active.y - 64 : active.y + 14,
            transform: "translateX(-50%)",
          }}
        >
          <div className="font-CircularStdBlack text-text-1">
            {active.date.format("MMM D, YYYY")}
          </div>
          <div className="text-text-2 tabular-nums">
            {formatWatchTime(active.seconds)} watched · {formatCount(active.plays)} plays · {formatCount(active.impressions)} impressions
          </div>
        </div>
      )}
    </div>
  );
};

export default SecondsChart;
